class PredictiveSearch extends SearchForm {
  static MINIMUM_QUERY_LENGTH = 2;
  static REQUEST_TIMEOUT_MS = 8000;

  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.rawSearchTerm = '';
    this.failedSearchTerm = null;
    this.requestSequence = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', this.clearFailedSearch.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onSearchChromeClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    this.clearFailedSearch();
    const newSearchTerm = this.getQuery();

    this.abortPendingRequest();
    this.requestSequence += 1;
    this.searchTerm = newSearchTerm;
    this.rawSearchTerm = newSearchTerm;

    if (newSearchTerm.length < PredictiveSearch.MINIMUM_QUERY_LENGTH) {
      this.predictiveSearchResults.innerHTML = '';
      this.removeAttribute('results');
      this.closeResults(false);
      return;
    }

    this.getSearchResults(newSearchTerm, this.requestSequence);
  }

  onFormSubmit(event) {
    const selectedResult = this.querySelector('[aria-selected="true"] a');
    const hasFailedQuery = this.failedSearchTerm === this.getQuery();
    const isContinuousMobileSearch = window.matchMedia('(max-width: 749px)').matches && !hasFailedQuery;
    if (!this.getQuery().length || selectedResult || isContinuousMobileSearch) event.preventDefault();

    if (isContinuousMobileSearch && !selectedResult && this.getQuery().length >= PredictiveSearch.MINIMUM_QUERY_LENGTH) {
      this.requestSequence += 1;
      this.abortPendingRequest();
      this.getSearchResults(this.getQuery(), this.requestSequence);
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.rawSearchTerm = '';
      this.failedSearchTerm = null;
      this.abortPendingRequest();
      this.requestSequence += 1;
      this.predictiveSearchResults.innerHTML = '';
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (currentSearchTerm.length < PredictiveSearch.MINIMUM_QUERY_LENGTH) return;

    if (this.failedSearchTerm === currentSearchTerm) {
      this.open();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else if (!this.hasAttribute('loading')) {
      this.requestSequence += 1;
      this.getSearchResults(currentSearchTerm, this.requestSequence);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const openHeaderSearch = this.closest('details-modal.header__search');
      if (openHeaderSearch?.isOpen()) return;
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (this.getQuery().length < PredictiveSearch.MINIMUM_QUERY_LENGTH) this.closeResults(false);

    if (event.code === 'ArrowUp') {
      event.preventDefault();
      this.switchOption('up');
    } else if (event.code === 'ArrowDown') {
      event.preventDefault();
      this.switchOption('down');
    } else if (event.code === 'Enter') {
      this.selectOption();
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  onSearchChromeClick(event) {
    const tab = event.target.closest('[data-dd-search-tab]');
    if (!tab || !this.contains(tab)) return;

    const familyName = tab.dataset.ddSearchTab;
    this.querySelectorAll('[data-dd-search-tab]').forEach((candidate) => {
      const isActive = candidate === tab;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    this.querySelectorAll('[data-dd-search-family]').forEach((family) => {
      family.classList.toggle('is-active', family.dataset.ddSearchFamily === familyName);
    });
    this.clearSelectedOption();
    this.refreshSearchFocusTrap(tab);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const visibleOptions = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null && element.querySelector('a')
    );
    if (!visibleOptions.length) return;

    const selectedElement = this.querySelector('[role="option"][aria-selected="true"]');
    const selectedIndex = visibleOptions.indexOf(selectedElement);
    let activeElementIndex = direction === 'up' ? selectedIndex - 1 : selectedIndex + 1;

    if (selectedIndex === -1) activeElementIndex = direction === 'up' ? visibleOptions.length - 1 : 0;
    if (activeElementIndex < 0) activeElementIndex = visibleOptions.length - 1;
    if (activeElementIndex >= visibleOptions.length) activeElementIndex = 0;

    this.clearSelectedOption();
    const activeElement = visibleOptions[activeElementIndex];
    activeElement.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', activeElement.id);
    this.statusElement.textContent = '';
  }

  selectOption() {
    const selectedOption = this.querySelector('[role="option"][aria-selected="true"] a');
    if (selectedOption) selectedOption.click();
  }

  abortPendingRequest() {
    this.abortController.abort();
    this.abortController = new AbortController();
  }

  getSearchResults(searchTerm, requestId) {
    const queryKey = searchTerm.toLocaleLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm, requestId);
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({
      q: this.getShopifySearchQuery(searchTerm),
      section_id: 'predictive-search',
      'resources[type]': 'product,collection,article,page',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
    });

    const requestController = this.abortController;
    const requestTimeout = setTimeout(() => {
      if (requestController.signal.aborted || !this.isLatestRequest(searchTerm, requestId)) return;
      requestController.abort();
      this.renderRequestError();
    }, PredictiveSearch.REQUEST_TIMEOUT_MS);

    fetch(`${root}search/suggest?${params.toString()}`, { signal: requestController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search request failed with ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (requestController.signal.aborted || !this.isLatestRequest(searchTerm, requestId)) return;

        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const responseSection = responseDocument.querySelector('#shopify-section-predictive-search');
        if (!responseSection) throw new Error('Predictive search response section was not found');

        const resultsMarkup = responseSection.innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup, searchTerm, requestId);
        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (requestController.signal.aborted || error.name === 'AbortError' || error.code === 20) {
          searchDeferred?.reject(error);
          return;
        }
        if (!this.isLatestRequest(searchTerm, requestId)) return;
        searchDeferred?.reject(error);
        this.renderRequestError();
      })
      .finally(() => clearTimeout(requestTimeout));
  }

  getShopifySearchQuery(searchTerm) {
    const normalizedTerm = searchTerm.trim();
    if (!normalizedTerm || normalizedTerm.endsWith('*')) return normalizedTerm;

    // Match the full Search form's options[prefix]=last behavior by using
    // Shopify's native prefix-query operator for Predictive Search.
    return `${normalizedTerm}*`;
  }

  isLatestRequest(searchTerm, requestId) {
    return requestId === this.requestSequence && searchTerm === this.getQuery();
  }

  getTotalResultCount() {
    return Number.parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults, 10) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(new SearchUpdateEvent({ search: { query }, promise: deferred.promise }));
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    this.predictiveSearchResults.setAttribute('aria-busy', 'true');
    this.open();
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm, requestId) {
    if (!this.isLatestRequest(searchTerm, requestId)) return;
    this.failedSearchTerm = null;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.rawSearchTerm = searchTerm;
    this.updateViewAllUrl(this.rawSearchTerm);
    this.predictiveSearchResults.setAttribute('aria-busy', 'false');
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
    this.refreshSearchFocusTrap();
  }

  updateViewAllUrl(rawSearchTerm) {
    const viewAllLink = this.predictiveSearchResults.querySelector('[data-dd-search-view-all]');
    if (!viewAllLink) return;

    const root = window.Shopify?.routes?.root || '/';
    const searchTarget = `${root}search`;
    const searchUrl = /^https?:\/\//i.test(searchTarget)
      ? new URL(searchTarget)
      : new URL(searchTarget, window.location.origin);
    searchUrl.searchParams.set('q', rawSearchTerm);
    searchUrl.searchParams.set('type', 'product,article,page');
    searchUrl.searchParams.set('options[prefix]', 'last');
    viewAllLink.href = `${searchUrl.pathname}${searchUrl.search}`;
  }

  refreshSearchFocusTrap(elementToFocus = null) {
    const focusContainer = this.closest('details-modal')?.querySelector('[tabindex="-1"]');
    if (focusContainer && typeof refreshTrapFocus === 'function') refreshTrapFocus(focusContainer, elementToFocus);
  }

  renderRequestError() {
    this.clearSelectedOption();
    this.failedSearchTerm = this.getQuery();
    const errorMessage = document.createElement('p');
    errorMessage.setAttribute('role', 'status');
    errorMessage.textContent = this.dataset.errorText || 'Search is temporarily unavailable. Please try again.';

    const fallbackLink = document.createElement('a');
    fallbackLink.className = 'dd-search-view-all dd-cta dd-cta--primary';
    fallbackLink.setAttribute('data-dd-search-view-all', '');
    const fallbackFace = document.createElement('span');
    fallbackFace.className = 'dd-cta__face';
    const searchLabel = this.input.form.querySelector('[type="submit"]')?.getAttribute('aria-label') || 'Search';
    fallbackFace.textContent = searchLabel;
    fallbackLink.setAttribute('aria-label', `${searchLabel}: ${this.failedSearchTerm}`);
    fallbackLink.append(fallbackFace);

    const errorContainer = document.createElement('div');
    errorContainer.className = 'dd-search__utility-error';
    errorContainer.append(errorMessage, fallbackLink);
    this.predictiveSearchResults.replaceChildren(errorContainer);
    this.updateViewAllUrl(this.failedSearchTerm);
    this.predictiveSearchResults.setAttribute('aria-busy', 'false');
    this.removeAttribute('loading');
    this.removeAttribute('results');
    this.setLiveRegionText(errorMessage.textContent);
    this.open();
    this.refreshSearchFocusTrap(this.input);
  }

  clearFailedSearch() {
    if (this.failedSearchTerm === null) return;
    this.failedSearchTerm = null;
    this.predictiveSearchResults.replaceChildren();
    this.closeResults(false);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const status = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (status) {
      const internalQuery = this.getShopifySearchQuery(this.rawSearchTerm);
      const statusText = status.textContent.trim().replace(internalQuery, this.rawSearchTerm);
      this.setLiveRegionText(statusText);
    }
  }

  getResultsMaxHeight() {
    return Math.max(0, window.innerHeight - this.predictiveSearchResults.getBoundingClientRect().top);
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  clearSelectedOption() {
    this.querySelectorAll('[role="option"][aria-selected="true"]').forEach((option) => {
      option.setAttribute('aria-selected', 'false');
    });
    this.input.setAttribute('aria-activedescendant', '');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    this.clearSelectedOption();
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.setAttribute('aria-busy', 'false');
    this.predictiveSearchResults.removeAttribute('style');
    this.refreshSearchFocusTrap(this.input);
  }
}

customElements.define('predictive-search', PredictiveSearch);
